import React, { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Search, Loader2, Eye, MousePointerClick, FileText, User, Mail, Phone, ChevronDown, ChevronRight, ExternalLink, ClipboardCheck } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const PAGE_LABELS = {
  '/': 'Home', '/loan': 'Personal Loans', '/protect-credit': 'Protect Credit',
  '/build-credit': 'Financials', '/how-it-works': 'How It Works', '/faq': 'FAQ',
  '/privacy-policy': 'Privacy Policy', '/unsubscribe': 'Unsubscribe',
};
const pageLabel = (p) => PAGE_LABELS[p] || p;

const KindBadge = ({ kind }) => {
  const map = {
    pageview: { color: 'bg-sky-50 text-sky-700 border-sky-200', label: 'PAGEVIEW', Icon: Eye },
    click:    { color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'OFFER CLICK', Icon: MousePointerClick },
    submission: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'FORM SUBMIT', Icon: FileText },
    qualifier: { color: 'bg-violet-50 text-violet-700 border-violet-200', label: 'QUALIFIER ANSWER', Icon: FileText },
  };
  const c = map[kind] || map.pageview;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full border ${c.color}`}>
      <c.Icon className="w-3 h-3" /> {c.label}
    </span>
  );
};

export default function AdminJourney() {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const { toast } = useToast();

  const runQuery = async (searchTerm) => {
    setLoading(true);
    try {
      const res = await adminApi.journey(searchTerm);
      setData(res);
      const first = (res?.sessions || [])[0]?.session_id;
      if (first) setExpanded(new Set([first]));
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setLoading(false); }
  };

  // Auto-load the most-recent sessions on mount so the page is never blank.
  useEffect(() => { runQuery(''); /* eslint-disable-next-line */ }, []);

  const search = (e) => {
    e?.preventDefault?.();
    runQuery(q.trim());
  };

  const clearSearch = () => {
    setQ('');
    runQuery('');
  };

  const toggle = (sid) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(sid)) n.delete(sid); else n.add(sid);
      return n;
    });
  };

  const fmt = (v) => v ? new Date(v).toLocaleString() : '';

  return (
    <div data-testid="admin-journey-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-slate-900">Customer Journey</h1>
          <p className="text-slate-600 mt-1">Every visitor session with pageviews, offer clicks, qualifier answers and form submissions — newest first. Use the search to filter by name, email, or phone.</p>
        </div>
        {data && !loading && (
          <div className="text-xs text-slate-500">
            Showing <b>{data.sessions?.length || 0}</b> sessions{data.query ? <> for <span className="text-slate-700 font-semibold">"{data.query}"</span></> : ' (most recent)'}
          </div>
        )}
      </div>

      <form onSubmit={search} className="mb-6 flex items-center gap-2">
        <div className="relative flex-1 max-w-lg">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="journey-search-input"
                 className="pl-9 h-11" placeholder="Search by name, email, phone or ZIP…" />
        </div>
        <Button type="submit" disabled={loading} data-testid="journey-search-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white h-11">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1.5" /> Search</>}
        </Button>
        {data?.query && (
          <Button type="button" variant="outline" onClick={clearSearch} className="h-11" data-testid="journey-clear-btn">
            Show recent
          </Button>
        )}
      </form>

      {loading && (
        <div className="flex justify-center py-14 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      )}

      {!loading && data && data.sessions?.length === 0 && (
        <div className="rounded-xl bg-white border border-slate-200 p-10 text-center text-slate-500" data-testid="journey-empty-state">
          <User className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          {data.query
            ? <>No matching journeys for <b>"{data.query}"</b>. Try a different name, email, or phone.</>
            : <>No visitor sessions recorded yet. Once visitors land on the site their journeys will appear here.</>}
        </div>
      )}

      {!loading && data && data.sessions?.length > 0 && (
        <div className="space-y-3">
          {data.sessions.map((s) => {
            const id = s.identity || {};
            const isOpen = expanded.has(s.session_id);
            return (
              <div key={s.session_id} data-testid={`journey-session-${s.session_id}`} className="rounded-xl bg-white border border-slate-200 overflow-hidden">
                <button onClick={() => toggle(s.session_id)} data-testid={`journey-session-toggle-${s.session_id}`} className="w-full text-left px-5 py-4 flex flex-wrap items-center gap-3 hover:bg-slate-50">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-900">{id.name || 'Anonymous'}</span>
                      {id.email && <span className="text-xs text-slate-600 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {id.email}</span>}
                      {id.phone && <span className="text-xs text-slate-600 inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {id.phone}</span>}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Session <code>{s.session_id.length > 24 ? s.session_id.slice(0, 20) + '…' : s.session_id}</code> · last seen {fmt(s.last_seen)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="inline-flex items-center gap-1 text-sky-700"><Eye className="w-3.5 h-3.5" /> {s.pageviews}</span>
                    <span className="inline-flex items-center gap-1 text-amber-700"><MousePointerClick className="w-3.5 h-3.5" /> {s.clicks}</span>
                    <span className="inline-flex items-center gap-1 text-violet-700" title="Qualifier answers"><ClipboardCheck className="w-3.5 h-3.5" /> {s.qualifiers || 0}</span>
                    <span className="inline-flex items-center gap-1 text-emerald-700"><FileText className="w-3.5 h-3.5" /> {s.submissions}</span>
                  </div>
                </button>
                {isOpen && (
                  <ol className="px-5 pb-5 space-y-3" data-testid="journey-timeline">
                    {s.timeline.map((ev, i) => (
                      <li key={i} data-testid={`journey-event-${ev.kind}`} className="flex gap-3">
                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 font-bold text-xs flex items-center justify-center shrink-0 mt-1">{i + 1}</div>
                        <div className="flex-1 rounded-lg border border-slate-200 p-3 bg-slate-50">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <KindBadge kind={ev.kind} />
                              {ev.kind === 'pageview' && <span className="font-semibold text-slate-900">{pageLabel(ev.page)} <span className="text-xs text-slate-400 font-normal">{ev.page}</span></span>}
                              {ev.kind === 'click' && <span className="font-semibold text-slate-900">Clicked <code className="text-emerald-700">{ev.short_url || ev.slug}</code></span>}
                              {ev.kind === 'submission' && <span className="font-semibold text-slate-900">Submitted <span className="text-xs text-slate-500 uppercase tracking-wider">{ev.type}</span> form</span>}
                              {ev.kind === 'qualifier' && (
                                <span className="font-semibold text-slate-900">
                                  Answered qualifier for <span className="text-violet-700">{ev.data?.offer_title || 'offer'}</span>
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500">{fmt(ev.at)}</div>
                          </div>
                          {ev.kind === 'qualifier' && (
                            <div className="mt-1.5 text-sm">
                              <span className="text-slate-500">"{ev.data?.question || 'Question'}"</span>
                              <span className="mx-1.5 text-slate-400">→</span>
                              <span className="font-bold text-violet-700">{ev.data?.answer_label ?? ev.data?.answer}</span>
                            </div>
                          )}
                          {ev.kind === 'click' && (
                            <div className="mt-1.5 text-xs text-slate-700 truncate">→ <a href={ev.final_url} target="_blank" rel="noopener" className="text-emerald-700 hover:underline inline-flex items-center gap-1">{ev.final_url} <ExternalLink className="w-3 h-3" /></a></div>
                          )}
                          {(ev.params || ev.data) && Object.keys(ev.params || ev.data || {}).length > 0 && (
                            <div className="mt-2 text-[11px] text-slate-600 flex flex-wrap gap-x-3 gap-y-0.5">
                              {Object.entries(ev.params || ev.data).filter(([k]) => !['__search'].includes(k)).slice(0, 12).map(([k, v]) => (
                                <span key={k}><span className="text-slate-400">{k}:</span> <span className="text-slate-800 font-medium">{String(v).length > 40 ? String(v).slice(0, 40) + '…' : String(v)}</span></span>
                              ))}
                            </div>
                          )}
                          {ev.referrer && <div className="mt-1 text-[11px] text-slate-400 truncate">Referrer: {ev.referrer}</div>}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
